import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { AlarmOption, PatientBehaviorDataModel, PatientBehaviorModel, PatientBehaviorType, PatientTargetDataModel, PatientTargetModel } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { ABCDataPanelComponent } from '../abc-data-panel/abc-data-panel.component';
import { AlertService } from '../../../shared/services/alert.service';
@Component({
  selector: 'app-box-behavior-interval',
  templateUrl: './box-behavior-interval.component.html',
  standalone: false,
})
export class BoxBehaviorIntervalComponent extends BaseBoxComponent implements AfterViewInit, OnInit {
  @Input() text: string | undefined = '-';
  @Input() amountOfTimes: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() behaviorID: number = 0;
  @Input() Duration: number = 60;
  @Input() behavior: PatientBehaviorModel | undefined = undefined;
  @Input() isPreview?: boolean;
  timerInitialValue: number = 60;
  behaviorData: PatientBehaviorDataModel[] = [];
  abcData: PatientBehaviorDataModel[] = [];

  behaviorType: PatientBehaviorType = PatientBehaviorType.Interval;

  @ViewChild('abcDataPanel') abcDataPanel!: ABCDataPanelComponent;

  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  private lastCompletedStartTime: number = 0;
  intervals: { ID?: number, happened: boolean; RecordedDate: string; FormattedDate: string;}[] = [];  
  happened: boolean = false;

  timeOut: any;
  alarmTimeout: any;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    this.isBehaviorBox = true; // Mark this as a behavior box to skip repositioning
    effect(() => {
      this.patientSessionService.dataChanged();
      const behaviorData = this.patientSessionService.getBehaviorData(this.behaviorID);
      if (behaviorData) {
        this.behaviorData = behaviorData.filter(t => !t.DeletedDate);
        this.intervals = this.behaviorData.map((t: PatientBehaviorDataModel) => {
          if (!t.TextValue) {
            t.TextValue = '';
          }
          const happened = (t.IntValue ?? 0) > 0;
          return {
            ID: t.ID??undefined,
            happened,
            RecordedDate: t.RecordedDate ?? '', FormattedDate: this.formatDate(new Date(t.RecordedDate ?? ''))
          };

        });

        
        
        this.amountOfTimes = this.intervals.length;
        
        this.abcData = this.behaviorData.filter((data) => data.Notes != null && data.Notes != undefined);

      }
      else {
        this.behaviorData = [];
        this.intervals = [];
      }
    });

    effect(() => {
      this.patientSessionService.dataChanged();
      this.syncFromRunningData();
    });
  }

  ngOnInit() {
    this.timerInitialValue = this.Duration ?? 60;
    this.timerValue = this.timerInitialValue;
    this.syncFromRunningData();
  }

  
  override doLongPress(): void {
    this.showExtra2();
    setTimeout(() => {
      //scroll tabs to end
      const tabs = document.querySelectorAll('.abc-tabs');
      tabs.forEach(tab => {
        tab.scrollLeft = tab.scrollWidth;
      });
    }, 100);
  }
  

  startTimer() {
    const running = this.patientSessionService.getTargetsRunningData(
      this.getSessionID(),
      this.getRunningTargetID()
    );
    const sharedStartTime = running?.Data?.startTime ?? 0;
    const sharedStillRunning =
      !!running?.Data?.isStarted &&
      sharedStartTime > 0 &&
      this.remainingSeconds(sharedStartTime) > 0;

    if (this.isStarted || sharedStillRunning) {
      if (!this.isStarted && sharedStillRunning) {
        this.syncFromRunningData();
      }
      this.showExtra();
      return;
    }

    this.isStarted = true;
    this.startTime = Date.now();
    this.happened = false;
    this.saveTargetRunningData();
    this.updateTimer();
  }


  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(this.getSessionID(), this.getRunningTargetID(), {
      isIntervalBehavior: true,
      isStarted: this.isStarted,
      startTime: this.startTime,
      timerInitialValue: this.timerInitialValue,
      timerValue: this.timerValue,
      happened: this.happened,
      lastCompletedStartTime: this.lastCompletedStartTime,
    });
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      if (this.timerValue <= 0) {
        const shouldRingAlarm = this.behavior?.AlarmOption === AlarmOption.AlarmAtEnd;
        this.ringAlarmIfNeeded(
          this.behavior?.AlarmOption ?? AlarmOption.NoAlarm,
          this.timerValue,
          this.behavior?.Interval ?? 60,
          this.behavior?.AlarmSoundOption ?? 1,
          false
        );

        if (this.alarmTimeout) {
          return;
        }
        if (shouldRingAlarm) {
          this.alarmTimeout = setTimeout(() => {
            this.completeInterval();
          }, 100);
        } else {
          this.completeInterval();
        }
        return;
      }

      this.ringAlarmIfNeeded(
        this.behavior?.AlarmOption ?? AlarmOption.NoAlarm,
        this.timerValue,
        this.behavior?.Interval ?? 60,
        this.behavior?.AlarmSoundOption ?? 1,
        false
      );

      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
      this.timeOut = setTimeout(() => {
        this.updateTimer();
      }, 1000);
    }else {
      this.clearTimerTimeouts();
    }
    }

  private getCurrentTimerValue(): number {
    return this.remainingSeconds(this.startTime);
  }

  private remainingSeconds(startTime: number): number {
    if (!startTime) {
      return this.timerInitialValue;
    }
    return Math.max(
      0,
      this.timerInitialValue - Math.floor((Date.now() - startTime) / 1000)
    );
  }

  private syncFromRunningData() {
    const targetsRunningData = this.patientSessionService.getTargetsRunningData(
      this.getSessionID(),
      this.getRunningTargetID()
    );
    if (targetsRunningData) {
      const wasStarted = this.isStarted;
      this.isStarted = targetsRunningData.Data.isStarted;
      this.startTime = targetsRunningData.Data.startTime;
      this.lastCompletedStartTime =
        targetsRunningData.Data.lastCompletedStartTime ?? 0;
      this.timerValue = this.isStarted
        ? this.getCurrentTimerValue()
        : targetsRunningData.Data.timerValue ?? this.timerInitialValue;
      this.happened = targetsRunningData.Data.happened ?? false;
      if (wasStarted && !this.isStarted &&
          this.lastCompletedStartTime === this.startTime &&
          this.getCurrentTimerValue() <= 0 && !this.isAlarmRinging) {
        this.ringAlarmIfNeeded(
          this.behavior?.AlarmOption ?? AlarmOption.NoAlarm,
          0,
          this.behavior?.Interval ?? 60,
          this.behavior?.AlarmSoundOption ?? 1,
          false
        );
      }
      if (this.isStarted && !wasStarted) {
        if (this.getCurrentTimerValue() <= 0) {
          this.completeInterval();
          return;
        }
        this.clearTimerTimeouts();
        this.timeOut = setTimeout(() => {
          this.updateTimer();
        }, 10);
      }
    } else {
      this.isStarted = false;
      this.startTime = 0;
      this.timerValue = this.timerInitialValue;
      this.happened = false;
    }
  }

  completeInterval() {
    this.clearTimerTimeouts();
    const startTime = this.startTime;
    if (!this.patientSessionService.completeInterval(this.getSessionID(), this.getRunningTargetID(), startTime)) {
      this.resetLocalTimerState();
      this.closeExtra();
      return;
    }

    this.lastCompletedStartTime = startTime;
    this.resetLocalTimerState();
    this.saveTargetRunningData();
    this.closeExtra();
  }

  cancelTimer() {
    this.completeInterval();
  }

  override ngOnDestroy() {
    this.clearTimerTimeouts();
    super.ngOnDestroy();
  }

  private getSessionID(): number {
    return this.patientSessionService.getPatientSession()?.ID ?? -1;
  }

  private getRunningTargetID(): number {
    return 900000000 + this.behaviorID;
  }

  private resetLocalTimerState() {
    this.isStarted = false;
    this.timerValue = this.timerInitialValue;
    this.happened = false;
    this.startTime = 0;
  }

  private clearTimerTimeouts() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = null;
    }
    if (this.alarmTimeout) {
      clearTimeout(this.alarmTimeout);
      this.alarmTimeout = null;
    }
  }

  timerString() {
    const minutes = Math.floor(this.timerValue / 60);
    const seconds = this.timerValue % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  lastInterval() {
    if (this.intervals.length > 0) {
      const lastInterval = this.intervals[this.intervals.length - 1];
      return `${lastInterval.happened ? 'Happened' : "Didn't happen"}`;
    } else {
      return '';
    }
  }

  setHappened(event: any) {
    this.happened = event.checked;
    this.saveTargetRunningData();
  }

  setExtraBoxes(): void {
    const dropdowns = document.querySelectorAll('.p-overlay.p-component');
    //convert Element to ElementRef
    this.extraBoxes = Array.from(dropdowns).map(dropdown => new ElementRef(dropdown));
    this.extraBoxes.push(this.extraBox);
    this.extraBoxes.push(this.extraBox2);
  }

  addFirstABCData() {
    const currentDate = new Date();
    const formattedDate = this.formatDate(currentDate);
    let behaviorData: PatientBehaviorDataModel = {
      IntValue: 0,
      TextValue: '0',
      PatientBehaviorID: this.behaviorID,
      RecordedDate: currentDate.toServerString(),
    };
    this.patientSessionService.addBehaviorData(behaviorData);
    this.addABCData({ happened: false, RecordedDate: currentDate.toServerString(), FormattedDate: formattedDate });
  }
  
  addABCData(interval: { ID?: number, happened: boolean, RecordedDate: string, FormattedDate: string }) {
    this.closeExtra2();
    this.abcDataPanel.initializeABCData(interval.RecordedDate);
    this.abcDataPanel.setEditingABCData(interval.RecordedDate);
    this.showExtra();
    setTimeout(() => {
      //scroll tabs to end
      const tabs = document.querySelectorAll('.abc-tabs');
      tabs.forEach(tab => {
        tab.scrollLeft = tab.scrollWidth;
      });
    }, 100);
  }

  hasABCData(interval: { ID?: number, happened: boolean, RecordedDate: string, FormattedDate: string }): boolean {
    return !!this.abcData.find(data => data.RecordedDate === interval.RecordedDate);
  }

  seeABCData(interval: { ID?: number, happened: boolean, RecordedDate: string, FormattedDate: string }) {
    this.closeExtra2();
    this.abcDataPanel.setEditingABCData(interval.RecordedDate);
    this.showExtra();
  }

  deleteData(data: any) {
    //confirm the deletion
    const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
    confirmed.subscribe(result => {
      if (result) {
        let behaviorDataToSave: PatientBehaviorDataModel | undefined = this.behaviorData.find(t =>
          t.RecordedDate === data.RecordedDate
        );

        if (behaviorDataToSave) {
          console.log('Updating existing anecdotal data:', behaviorDataToSave);
          behaviorDataToSave.DeletedDate = new Date().toServerString();

          this.patientSessionService.saveBehaviorData(behaviorDataToSave);
        }
      }
    });
  }
}
